import { toast } from 'react-toastify';
import { useBoolean } from 'react-use';
import { useCallback } from 'react';
import { useCaseResourcesDataQuery, useAssignUserToCaseGroup, useCaseByIdQuery } from '@/queries/case';
import { useRemoveReserveResource, useReserveResource } from '@/queries/orchestrator';
import { useUpdateOpoContacts } from '../useUpdateOpoContacts';
import { Uuid } from '@/api/types';
import { CaseUserGroup, OpoContactWithPrimary } from '@/api/apis/case';
import { retrieveUnifiedError } from '@/api/utils';
import { getMutationItems, getUsersAssignmentPayload, getShouldSetPrimary } from './utils';
import { useUserSettingsStore } from '@/store/user-settings.store';
import { primaryAvailableGroups } from '@/constants/user.constants';
import { RoleKeyedPartial } from '@/types';

type Props = {
  caseId: Uuid;
  clinicalSpecialistIds: string[];
  newClinicalSpecialistIds: string[];
  surgeonIds: string[];
  newSurgeonIds: string[];
  assignedDevicesIds: string[];
  newDevicesIds: string[];
  assignedUsersByRoles: RoleKeyedPartial<string[]>;
  usersToAssign: RoleKeyedPartial<string[]>;
  assignedContacts: OpoContactWithPrimary[];
  opoContactsToAssign: OpoContactWithPrimary[];
  useExtendedResourcesInfo: boolean;
  enableCaseByIdQuery?: boolean;
  isOpoCase?: boolean;
  onSetPrimaryModalOpen?: () => void;
  onSave: () => void;
  isPrimaryUnassigned?: boolean;
};

export const useMutateResourcesAssignment = ({
  caseId,
  assignedUsersByRoles,
  usersToAssign,
  clinicalSpecialistIds,
  newClinicalSpecialistIds,
  surgeonIds,
  newSurgeonIds,
  assignedDevicesIds,
  newDevicesIds,
  assignedContacts,
  opoContactsToAssign,
  useExtendedResourcesInfo,
  enableCaseByIdQuery = true,
  isOpoCase = false,
  isPrimaryUnassigned,
  onSetPrimaryModalOpen,
  onSave,
}: Props) => {
  const { refetch: refetchCase } = useCaseByIdQuery(caseId, enableCaseByIdQuery);
  const { refetch: refetchCaseResourcesData } = useCaseResourcesDataQuery(caseId, useExtendedResourcesInfo);
  const { mutateAsync: mutateUsersAssign } = useAssignUserToCaseGroup();
  const { mutateAsync: reserveResource } = useReserveResource();
  const { mutateAsync: removeReserve } = useRemoveReserveResource();
  const getOpoContactsMutations = useUpdateOpoContacts(isOpoCase);
  const [isSubmitting, setIsSubmitting] = useBoolean(false);
  const blockIsPrimaryModal = useUserSettingsStore((state) => state.blockPrimaryModalCaseIds).includes(caseId);
  let groupsWithPrimaryAssigned: CaseUserGroup[] = [];

  const getAssignedUsersMutations = async () => {
    const payload = getUsersAssignmentPayload(assignedUsersByRoles, usersToAssign);
    if (payload.length) {
      await mutateUsersAssign({ caseId, payload });
    }

    groupsWithPrimaryAssigned = [
      ...new Set(payload.filter((u) => primaryAvailableGroups.includes(u.group)).map((u) => u.group)),
    ];
  };

  const getResourcesMutations = useCallback(
    async (oldIds: string[], newIds: string[], key: string) => {
      const [resourceToAttach, resourceToDetach] = getMutationItems(oldIds, newIds);

      if (resourceToAttach.length) {
        await reserveResource({ caseId, [key]: resourceToAttach });
      }

      if (resourceToDetach.length) {
        await removeReserve({ caseId, [key]: resourceToDetach });
      }

      return resourceToAttach.length > 0;
    },
    [reserveResource, caseId, removeReserve],
  );

  const saveAssignment = async () => {
    setIsSubmitting(true);
    try {
      await getAssignedUsersMutations();
      await getOpoContactsMutations(assignedContacts, opoContactsToAssign, caseId);
      const specialistsReserved = await getResourcesMutations(
        clinicalSpecialistIds,
        newClinicalSpecialistIds,
        'clinicalSpecialistIds',
      );
      const surgeonsReserved = await getResourcesMutations(surgeonIds, newSurgeonIds, 'surgeonIds');
      const deviceReserved = await getResourcesMutations(assignedDevicesIds, newDevicesIds, 'ocsConsoleIds');
      const logisticsResourcesReserved = specialistsReserved || surgeonsReserved || deviceReserved;
      toast.success(logisticsResourcesReserved ? 'Resources reserved successfully' : 'Resources assigned successfully');
      onSave();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    } finally {
      if (enableCaseByIdQuery) {
        const { data } = await refetchCase();

        const shouldSetPrimary = getShouldSetPrimary(
          data?.assignedEmployees,
          groupsWithPrimaryAssigned,
          !!isPrimaryUnassigned,
        );
        if (!blockIsPrimaryModal && shouldSetPrimary && groupsWithPrimaryAssigned.length) {
          onSetPrimaryModalOpen?.();
        }
      }

      if (useExtendedResourcesInfo) {
        await refetchCaseResourcesData();
      }
      setIsSubmitting(false);
    }
  };

  return { isSubmitting, saveAssignment };
};
